import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { Edit, Delete } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import MealItem from '../../../app/data/models/mealItem';
import { fetchMealItemListAsync } from './mealItemListSlice';
import MealItemForm from './MealItemForm';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import StyledTableRow from '../../../app/components/StyledTableRow';
import FillWidthCell from '../../../app/components/FillWidthCell';
import EmptyScreen from '../../../app/components/EmptyScreen';

const MealItemList = () => {
  const { t } = useTranslation();
  const { restaurantId: restaurantIdString } = useParams<{
    restaurantId: string;
  }>();
  const restaurantId = restaurantIdString || '';
  const { mealItems, status } = useAppSelector((state) => state.mealItems);
  const dispatch = useAppDispatch();
  const [editMode, setEditMode] = useState(false);
  const [selectedMealItem, setSelectedMealItem] = useState<
    MealItem | undefined
  >(undefined);
  const [loading] = useState(false);
  const [target] = useState(0);

  useEffect(() => {
    dispatch(fetchMealItemListAsync(restaurantId));
  }, [restaurantId, dispatch]);

  const handleSelectMealItem = (mealItem: MealItem) => {
    setSelectedMealItem(mealItem);
    setEditMode(true);
  };

  const cancelEdit = () => {
    if (selectedMealItem) setSelectedMealItem(undefined);
    setEditMode(false);
  };

  const handleDeleteMealItem = (id: string) => {
    console.log(id);
  };

  const handleMealItemClick = (mealItem: MealItem) => {
    console.log(mealItem.id);
  };

  if (status.includes('pending')) return <LoadingComponent />;

  if (editMode)
    return (
      <MealItemForm
        restaurantId={restaurantId}
        mealItemId={selectedMealItem?.id}
        cancelEdit={cancelEdit}
      />
    );

  if (!mealItems || mealItems.length === 0)
    return (
      <EmptyScreen
        title={t('meal-item.list.title')}
        emptyText={t('meal-item.list.empty')}
        onPrimaryClick={() => setEditMode(true)}
      />
    );

  return (
    <>
      <ListTitleBox
        title={t('meal-item.list.title')}
        onPrimaryClick={() => setEditMode(true)}
      />
      <TableContainer component={Paper} sx={{ width: '100%', mb: 2 }}>
        <Table aria-label={t('meal-item.list.title')}>
          <TableHead>
            <TableRow>
              <TableCell width="50px" align="left">
                #
              </TableCell>
              <FillWidthCell align="left">
                {t('meal-item.list.header.name')}
              </FillWidthCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mealItems.map((mealItem, index) => (
              <StyledTableRow
                key={mealItem.id}
                onClick={() => handleMealItemClick(mealItem)}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  width: '100%',
                }}
              >
                <TableCell width="50px" align="left" component="th" scope="row">
                  {index + 1}
                </TableCell>
                <TableCell align="left">{mealItem.name}</TableCell>
                <TableCell align="right">
                  <Box
                    display="flex"
                    sx={{
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <IconButton
                      sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectMealItem(mealItem);
                      }}
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                    <LoadingButton
                      sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                      loading={loading && target === mealItem.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMealItem(mealItem.id);
                      }}
                      color="error"
                      size="small"
                    >
                      <Delete />
                    </LoadingButton>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default MealItemList;
